import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import CustomTable from 'common/components/CustomTable'
import Kpi from 'common/components/Kpi'
import 'dayjs/locale/es'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useMemo, useRef, useState } from 'react'
import { OBJETIVOS_OPTIMIZACION } from '../../data/objetivos'
import { PlanificacionesHelper } from '../../helpers/PlanificacionesHelper'
import useFetchKpis from '../../hooks/useFetchKpis'
import useFetchPlanificaciones from '../../hooks/useFetchPlanificaciones'
import { usePlanificaciones } from '../../providers/PlanificacionesProvider'

export default function DashboardView() {
  const {
    response: kpis,
    loading: loadingKpis,
    error: errorKpis,
    refetch: refetchKpis,
  } = useFetchKpis()
  const {
    response: planificaciones,
    loading: loadingPlanificaciones,
    error: errorPlanificaciones,
    refetch: refetchPlanificaciones,
  } = useFetchPlanificaciones()

  // Acceder al contexto para navegación
  const { goToEdit, goToCreate, refreshTrigger } = usePlanificaciones()

  const ordenPlanif = useMemo(() => {
    if (!planificaciones) return []
    return [...planificaciones].sort((a, b) => b.id - a.id)
  }, [planificaciones])

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Estado para el diálogo de confirmación de eliminación
  const [dialogoEliminar, setDialogoEliminar] = useState({
    open: false,
    planificacion: null,
  })

  // Función para abrir el diálogo de confirmación
  const handleEliminarClick = (planificacion) => {
    setDialogoEliminar({ open: true, planificacion })
  }

  // Función para cerrar el diálogo
  const handleCancelarEliminar = () => {
    setDialogoEliminar({ open: false, planificacion: null })
  }

  // Función para confirmar la eliminación
  const handleConfirmarEliminar = async () => {
    const { planificacion } = dialogoEliminar
    try {
      await PlanificacionesHelper.deletePlanificacion(planificacion.id)
      enqueueSnackbar(`Planificación ID ${planificacion.id} eliminada correctamente`, {
        variant: 'success',
      })
      refetchPlanificaciones()
      setDialogoEliminar({ open: false, planificacion: null })
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || 'Error al eliminar la planificación', {
        variant: 'error',
      })
    }
  }

  // Refrescar datos cuando refreshTrigger cambia
  useEffect(() => {
    if (refreshTrigger > 0) {
      refetchKpis()
      refetchPlanificaciones()
    }
  }, [refreshTrigger, refetchKpis, refetchPlanificaciones])

  // Polling: refrescar la tabla mientras haya planificaciones en estado 'O'
  const pollingRef = useRef(null)
  useEffect(() => {
    const hayOptimizando = planificaciones?.some((p) => p.status === 'O')
    if (!hayOptimizando) {
      clearInterval(pollingRef.current)
      return
    }
    pollingRef.current = setInterval(() => {
      refetchPlanificaciones()
    }, 15000)
    return () => clearInterval(pollingRef.current)
  }, [planificaciones, refetchPlanificaciones])

  // Configuración de columnas para la tabla
  const columns = useMemo(() => {
    const allColumns = [
      {
        key: 'ID',
        value: 'id',
        width: 80,
        sortable: true,
        hideOnMobile: false,
      },
      {
        key: 'Fecha Inicio',
        value: 'fecha_inicio',
        width: 160,
        sortable: true,
        hideOnMobile: true,
        renderCell: (row) => {
          const fechaStr =
            row.fecha_inicio?.replace(/[Z]$/, '').replace(/[+-]\d{2}:\d{2}$/, '') || ''
          const fecha = new Date(fechaStr)
          return fecha.toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        },
      },
      {
        key: 'Descripción',
        value: 'descripcion',
        width: 160,
        flex: 1,
        sortable: true,
        hideOnMobile: false,
      },
      {
        key: 'Segundos Máximo',
        value: 'segundos_maximo',
        width: 100,
        sortable: true,
        hideOnMobile: true,
        renderCell: (row) => row.segundos_maximo || '-',
      },
      {
        key: 'Criterio Optimización',
        value: 'criterio_optimizacion',
        width: 200,
        hideOnMobile: true,
        renderCell: (row) => {
          const criterio = row.criterio_optimizacion
          if (!criterio) return '-'

          const esHibrido = typeof criterio === 'object' && criterio.type === 'hybrid'
          const objetivo = esHibrido
            ? null
            : OBJETIVOS_OPTIMIZACION.find((o) => o.name === criterio)
          const label = esHibrido ? 'Híbrido' : (objetivo?.label ?? String(criterio))
          const tooltipText = esHibrido
            ? 'Combinación ponderada de varios criterios'
            : (objetivo?.description ?? '')
          return (
            <Tooltip title={tooltipText} arrow>
              <Chip label={label} color="primary" size="small" sx={{ fontWeight: 'bold' }} />
            </Tooltip>
          )
        },
      },
      {
        key: 'Restricciones',
        value: 'restricciones_planificacion',
        width: 250,
        hideOnMobile: true,
        renderCell: (row) => (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              alignItems: 'center',
              minHeight: '100%',
            }}
          >
            {row.restricciones_planificacion && row.restricciones_planificacion.length > 0 ? (
              row.restricciones_planificacion.map((restriccion, idx) => (
                <Chip
                  key={idx}
                  label={restriccion.descripcion_restriccion}
                  color="secondary"
                  size="small"
                  title={restriccion.codigo_restriccion}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Sin restricciones
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: 'Estado',
        value: 'status',
        width: 80,
        sortable: true,
        hideOnMobile: false,
        renderCell: (row) => {
          const STATUS_MAP = {
            Z: { color: '#4caf50', label: 'Finalizada' },
            O: { color: '#ff9800', label: 'Optimizando' },
            X: { color: '#f44336', label: 'Error' },
            A: { color: '#9e9e9e', label: 'Pendiente' },
          }
          const { color, label } = STATUS_MAP[row.status] || STATUS_MAP.A
          const tooltipText =
            row.status === 'X' && row.mensaje_error ? `${label}: ${row.mensaje_error}` : label
          return (
            <Tooltip title={tooltipText} arrow>
              <FiberManualRecordIcon sx={{ color, fontSize: 20 }} />
            </Tooltip>
          )
        },
      },
      {
        key: 'Acciones',
        value: 'acciones',
        width: 120,
        sortable: false,
        hideOnMobile: false,
        renderCell: (row) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <IconButton size="large" variant="outlined" onClick={() => goToEdit(row)}>
              <EditOutlinedIcon fontSize="medium" />
            </IconButton>
            <IconButton
              size="large"
              variant="outlined"
              color="error"
              onClick={() => handleEliminarClick(row)}
            >
              <DeleteOutlineIcon fontSize="medium" />
            </IconButton>
          </Box>
        ),
      },
    ]

    // Filtrar columnas según el tamaño de pantalla
    return isMobile ? allColumns.filter((col) => !col.hideOnMobile) : allColumns
  }, [isMobile, goToEdit])

  const loading = loadingKpis || loadingPlanificaciones
  const error = errorKpis || errorPlanificaciones

  return loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </Box>
  ) : error ? (
    <div>Error: {error}</div>
  ) : (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Grid de tarjetas de Kpis */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: { xs: 3, md: 4 } }}>
        <Kpi
          id="kpi-pedidos"
          cssClass="kpi-card"
          title="Pedidos"
          subtitle="Total de pedidos únicos"
          value={kpis?.numero_pedidos}
          sx={{ flex: '1 1 0' }}
        />
        <Kpi
          id="kpi-maquinas"
          cssClass="kpi-card"
          title="Máquinas"
          subtitle="Máquinas disponibles"
          value={kpis?.numero_maquinas}
          sx={{ flex: '1 1 0' }}
        />
        <Kpi
          id="kpi-actividades"
          cssClass="kpi-card"
          title="Actividades"
          subtitle="Actividades disponibles"
          value={kpis?.numero_actividades}
          sx={{ flex: '1 1 0' }}
        />
        <Kpi
          id="kpi-combinaciones"
          cssClass="kpi-card"
          title="Combinaciones"
          subtitle="Combinaciones Actividades-Máquinas"
          value={kpis?.numero_combinaciones}
          sx={{ flex: '1 1 0' }}
        />
        <Kpi
          id="kpi-transiciones"
          cssClass="kpi-card"
          title="Transiciones"
          subtitle="Total de transiciones"
          value={kpis?.numero_transiciones}
          sx={{ flex: '1 1 0' }}
        />
        <Kpi
          id="kpi-act-pedido"
          cssClass="kpi-card"
          title="Media Act/Ped"
          subtitle="Media de actividades por pedido"
          value={kpis?.media_actividades_por_pedido}
          sx={{ flex: '1 1 0' }}
        />
      </Box>

      {/* Sección Planificaciones */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Planificaciones
          </Typography>
          <Button
            onClick={goToCreate}
            variant="contained"
            color="primary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Nueva Planificación
          </Button>
        </Box>

        <CustomTable
          dataSource={ordenPlanif}
          fields={columns}
          loading={loading}
          initialPageSize={10}
          hideColumnFilter={true}
          showToolbar={true}
          rowid="id"
          autoHeight={true}
          onRowDoubleClick={(params) => goToEdit(params.row)}
        />
      </Paper>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog open={dialogoEliminar.open} onClose={handleCancelarEliminar}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar la planificación{' '}
            <strong>ID {dialogoEliminar.planificacion?.id}</strong>
            {dialogoEliminar.planificacion?.descripcion && (
              <> - {dialogoEliminar.planificacion.descripcion}</>
            )}
            ?
            <br />
            <br />
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelarEliminar} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmarEliminar} variant="contained" color="error" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
